import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { AssistantSource } from "@/api/assistant";

/**
 * Згортаний блок «Джерела» (AC-AI-CITE / AC-AI-SRC-DATE).
 * Заголовок: «Джерела · N · оновлено DD.MM.YYYY» (мокап v2) + per-item версія.
 * indexedAt порожнє/невалідне → graceful fallback на «v{indexVersion}» (UI не ламається).
 * Рендериться ЛИШЕ коли sources непорожні (sources-gate enforced у Thread —
 * порожні sources → no-coverage стан, цей блок не монтується).
 */
export function AssistantSources({
  sources,
  indexVersion,
  indexedAt,
  defaultOpen = true,
}: {
  sources: AssistantSource[];
  indexVersion: string;
  indexedAt: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (sources.length === 0) return null;

  const updatedLabel = formatIndexedAt(indexedAt);

  return (
    <div className="mt-3 overflow-hidden rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between bg-muted px-3 py-2 text-left text-[11.5px] font-semibold text-muted-foreground hover:bg-muted/70"
      >
        <span>
          Джерела · <span className="text-primary-dark">{sources.length}</span>
          {updatedLabel ? (
            <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">оновлено {updatedLabel}</span>
          ) : indexVersion ? (
            <span className="ml-1.5 font-mono text-[10px] font-normal text-muted-foreground">v{indexVersion}</span>
          ) : null}
        </span>
        {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
      </button>
      {open && (
        <ul>
          {sources.map((s, i) => (
            <li
              key={`${s.kbDocId}-${s.section}-${i}`}
              className="flex items-center justify-between gap-2 border-t border-border-subtle px-3 py-2 text-xs"
            >
              <span className="min-w-0 truncate">
                <b className="font-semibold">{s.title}</b>
                <span className="ml-1 text-muted-foreground">→ {s.section}</span>
              </span>
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">v{s.indexVersion}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * AC-AI-SRC-DATE: ISO indexedAt → «DD.MM.YYYY». Дату беремо прямо з ISO-префікса
 * (без Date-парсингу → 0 TZ-зсуву); порожнє/невалідне → null (graceful, без дати).
 * Напр. "2026-06-14T23:41:34+03:00" → "14.06.2026".
 */
function formatIndexedAt(raw: string): string | null {
  const m = raw?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}
